/* ═══════════════════════════════════════════════════════════════
   SpecsTool — the full-bleed takeover behind the Specs widget's
   expand button. Moved verbatim from the old "Specs" tab in the
   pre-R1 Projects page (toolbar, DataTable, the .docx import flow, and the
   whole export configurator) — chrome restyled onto the kit
   (Dialog/Button/Input/Checkbox/SearchableSelect), logic untouched.

   Needs the FULL firm-wide forma list (masters + every project/spec
   copy) to resolve per-spec formatting exactly like the editor does
   — `ctx.formas` is project-scoped only, so this loads its own copy
   via FormaService, same as the old page did.
   ═══════════════════════════════════════════════════════════════ */

import { isProjectSpecPending } from '../../../../shared/spec-publish';
import React, { useEffect, useState } from 'react';
import { Plus, Upload, Download } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Icon } from '../../../components/ui/Icon';
import { Input } from '../../../components/ui/input';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../../components/ui/dialog';
import { SearchableSelect } from '../../../components/SearchableSelect';
import { DataTable, Column, GroupOption } from '../../../components/DataTable';
import EmptyState from '../../../components/EmptyState';
import { StatusBadge } from '../../../components/StatusBadge';
import { isArchived, activeOnly } from '../../../lib/archive';
import { isWeb } from '../../../lib/platform';
import { ProjectSpecService } from '../../../services/projectSpecService';
import { SpecService } from '../../../services/specService';
import {
  isWordFile,
  isLegacyWordFile,
  LEGACY_WORD_NOT_PROCESSED_MESSAGE,
} from '../../../../shared/wordFiles';
import { FormaService } from '../../../services/formaService';
import { TimeService } from '../../../services/timeService';
import { prepareSpecsForExport, exportSpecsViaServer } from '../../../services/specExportPipeline';
import { resolveProjectTokenContent } from '../../../components/ProjectFormaTokens';
import { ProjectService } from '../../../services/projectService';
import { latestProjectRow } from '../../../services/projectTokensForExport';
import { resolvePlaceholders, unboundPlaceholders } from '../../../../shared/forma-placeholders';
import { csiDivisionGroupLabel } from '../../../../shared/zone-layout';
import { realIssuances } from '../../../../shared/types';
import { hasPermission } from '../../../../shared/permissions';
import type { ProjectSpec, SpecMaster, StandardConfig } from '../../../../shared/types';
import type { ProjectWidgetCtx } from './context';
import { toAddress } from '../../../routing/address';

/** One file that did not make it, with a plain reason. */
interface FileFailure {
  file: string;
  reason: string;
}

/** Results of an export run that had at least one failure. */
interface ExportReport {
  okCount: number;
  failures: FileFailure[];
  warnings: string[];
  folder: string;
}

const VARIANT_NOT_FOUND = 'Variant not found';

const latestIssuance = (s: ProjectSpec) => realIssuances(s.issuances).slice(-1)[0]?.name || '';

export function SpecsTool({ ctx }: { ctx: ProjectWidgetCtx }) {
  const project = ctx.project;
  const projectSpecs = ctx.projectSpecs.data || [];

  const [showArchivedSpecs, setShowArchivedSpecs] = useState(false);
  const [importingDocx, setImportingDocx] = useState(false);
  const [importFailures, setImportFailures] = useState<FileFailure[] | null>(null);
  const [exportReport, setExportReport] = useState<ExportReport | null>(null);

  // Masters, only to name each spec's variant (the layer it was forked from).
  const [masterList, setMasterList] = useState<SpecMaster[]>([]);
  const [mastersFailed, setMastersFailed] = useState(false);
  useEffect(() => {
    SpecService.listMasters()
      .then((list) => {
        setMasterList(list);
        setMastersFailed(false);
      })
      .catch((err: unknown) => {
        setMasterList([]);
        setMastersFailed(true);
        ctx.toast(
          `Variant names could not be loaded: ${err instanceof Error ? err.message : 'Unknown error'}`,
          true
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const variantNameOf = (s: ProjectSpec): string => {
    if (!s.variantId) return 'Office Master';
    if (mastersFailed) return VARIANT_NOT_FOUND;
    const layer = masterList
      .find((m) => m.id === s.masterId)
      ?.layers?.find((l) => l.id === s.variantId);
    return layer?.name || VARIANT_NOT_FOUND;
  };

  // Firm-wide formas (masters + every project/spec copy) — needed to resolve
  // per-spec formatting exactly like the spec editor does; ctx.formas only
  // carries this project's copies.
  const [allFormas, setAllFormas] = useState<StandardConfig[]>([]);
  useEffect(() => {
    FormaService.list()
      .then(setAllFormas)
      .catch(() => setAllFormas([]));
  }, []);

  // ─── Export modal state ───
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportSelIds, setExportSelIds] = useState<Set<string>>(new Set());
  const [exportFormat, setExportFormat] = useState<'docx' | 'pdf' | 'both'>('docx');
  const [exportFormaId, setExportFormaId] = useState('');
  const exportFormaOptions = allFormas
    .filter((f: any) => !f.projectId || (f.projectId === project.id && !f.specId))
    .filter((f: any) => !isArchived(f) || f.id === exportFormaId)
    .map((f: any) => ({
      value: f.id,
      label: (f.docSettings?.standardName || 'Untitled') + (f.projectId ? '  · this project' : ''),
    }));
  const [exportDir, setExportDir] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportDeliverable, setExportDeliverable] = useState('');
  const [exportRevisionMarks, setExportRevisionMarks] = useState(false);
  const [exportDeliverables, setExportDeliverables] = useState<Array<{ id: string; name: string }>>(
    []
  );

  useEffect(() => {
    if (!showExportModal || !project.id) return;
    setExportSelIds(new Set(projectSpecs.map((s) => s.id)));
    TimeService.listDeliverables(project.id)
      .then((ds) => setExportDeliverables(ds.map((d) => ({ id: d.id, name: d.name }))))
      .catch(() => setExportDeliverables([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showExportModal]);

  // ─── Import raw .docx straight into this project's specs ───
  async function handleImportDocxToProject() {
    if (!project.id) return;
    const api = window.api;
    if (!api?.openDocxDialog || !api?.extractDocx) {
      ctx.toast('Import requires the desktop app', true);
      return;
    }
    const paths: string[] = await api.openDocxDialog();
    if (!paths?.length) return;
    setImportingDocx(true);
    let imported = 0;
    const failed: FileFailure[] = [];
    const now = new Date().toISOString();
    for (const p of paths) {
      const fname = p.split(/[/\\]/).pop() || p;
      let convertedDocx: string | null = null;
      try {
        if (!isWordFile(fname)) {
          failed.push({ file: fname, reason: 'Not a Word file' });
          continue;
        }
        let source = p;
        if (isLegacyWordFile(fname)) {
          // .doc / .dot: convert to .docx first (desktop only).
          if (!api.convertWordToDocx) {
            failed.push({ file: fname, reason: LEGACY_WORD_NOT_PROCESSED_MESSAGE });
            continue;
          }
          const conv = await api.convertWordToDocx(p);
          if (!conv?.ok) {
            failed.push({ file: fname, reason: conv?.message || 'Could not convert the file' });
            continue;
          }
          source = conv.docxPath;
          convertedDocx = conv.docxPath;
        }
        const result = await api.extractDocx(source);
        if (!result?.success) {
          failed.push({ file: fname, reason: result?.error || 'Could not read the file' });
          continue;
        }
        const coreContent = (result.blocks || []).map((b: any, i: number) => ({
          id: 'b-' + Date.now().toString(36) + '-' + i,
          level: b.level,
          text: b.text,
          bold: b.bold,
          italic: b.italic,
          underline: b.underline,
          blockType: b.blockType,
          tableData: b.tableData,
          imageData: b.imageData,
          type: 'fixed' as const,
        }));
        const spec = {
          id: 'ps-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8),
          projectId: project.id,
          masterId: '',
          masterVersion: 0,
          csiNumber: result.csiNumber || '',
          title: result.title || fname.replace(/\.[^.]+$/, ''),
          coreContent,
          systems: [],
          variants: [],
          compiledAt: now,
          lastEdited: now,
        } as ProjectSpec;
        await ProjectSpecService.save(spec);
        imported++;
      } catch (err: unknown) {
        failed.push({ file: fname, reason: err instanceof Error ? err.message : String(err) });
      } finally {
        // The converted temp .docx is consumed — remove it.
        if (convertedDocx) await window.api.cleanupConvertedDocx?.(convertedDocx);
      }
    }
    await ctx.projectSpecs.reload();
    setImportingDocx(false);
    if (imported > 0)
      ctx.toast(`Imported ${imported} spec${imported !== 1 ? 's' : ''} into ${project.name}`);
    if (failed.length > 0) {
      if (imported === 0) ctx.toast(`Imported 0 \u00b7 Failed ${failed.length}`, true);
      setImportFailures(failed);
    }
  }

  const bulkDeleteProjectSpecs = async (items: ProjectSpec[]) => {
    for (const s of items) await ProjectSpecService.delete(s.id);
    await ctx.projectSpecs.reload();
    ctx.toast(`Removed ${items.length} spec${items.length !== 1 ? 's' : ''}`);
  };

  const doArchiveProjectSpecs = async (items: ProjectSpec[], archived: boolean) => {
    try {
      for (const s of items) {
        await ProjectSpecService.setArchived(s.id, archived, ctx.authUser?.displayName);
      }
      await ctx.projectSpecs.reload();
      ctx.toast(
        `${archived ? 'Archived' : 'Restored'} ${items.length} spec${items.length !== 1 ? 's' : ''}`
      );
    } catch (err: any) {
      ctx.toast(
        `Those specs could not be ${archived ? 'archived' : 'restored'}: ${err.message || 'Unknown error'}`,
        true
      );
    }
  };

  const specColumns: Column<ProjectSpec>[] = [
    {
      key: 'csiNumber',
      label: 'CSI #',
      width: 100,
      sortable: true,
      getValue: (s) => s.csiNumber || '',
    },
    { key: 'title', label: 'Title', sortable: true, getValue: (s) => s.title || '' },
    {
      key: 'variant',
      label: 'Variant',
      width: 140,
      sortable: true,
      getValue: (s) => variantNameOf(s),
      render: (s) => <span className="text-sm text-subtle">{variantNameOf(s)}</span>,
    },
    {
      key: 'issuance',
      label: 'Latest Issuance',
      width: 140,
      sortable: true,
      getValue: (s) => latestIssuance(s),
      render: (s) => {
        const name = latestIssuance(s);
        if (!name) return <span className="italic text-subtle">—</span>;
        return (
          <span className="rounded-full bg-primary-soft px-2 py-0.5 text-xs font-semibold text-primary-text">
            {name}
          </span>
        );
      },
    },
    {
      key: 'editedBy',
      label: 'Last Edited By',
      width: 140,
      sortable: true,
      getValue: (s) => s.editedBy || '',
    },
    {
      key: 'lastEdited',
      label: 'Last Edited',
      width: 140,
      sortable: true,
      getValue: (s) => (s.lastEdited ? new Date(s.lastEdited).toLocaleDateString() : ''),
    },
    {
      key: 'status',
      label: 'Status',
      width: 130,
      getValue: (s) => (isArchived(s) ? 'archived' : 'active'),
      render: (s) =>
        isArchived(s) ? (
          <StatusBadge status="archived" />
        ) : isProjectSpecPending(s) ? (
          <StatusBadge status="pending_publish" label="Pending publish" variant="warning" />
        ) : null,
    },
  ];
  // Keyed to the COLUMN ('csiNumber') so it replaces that column's own
  // grouping instead of sitting beside it (DataTable merges by key).
  const specGroupOptions: GroupOption<ProjectSpec>[] = [
    {
      key: 'csiNumber',
      label: 'Division #',
      getValue: (s) => csiDivisionGroupLabel(s.csiNumber),
    },
  ];

  // The export reads each spec's last PUBLISHED copy (SYNC D12).
  const unpublishedSelected = projectSpecs.filter(
    (s) => exportSelIds.has(s.id) && isProjectSpecPending(s)
  ).length;

  function openExportModal() {
    // Preselect: this project's own forma copy → a master attached to this
    // project → most-used among the specs' bindings → nothing.
    const projectCopy = allFormas.find((f: any) => f.projectId === project.id && !f.specId);
    const attached = allFormas.find((f: any) => f.attachedProjectId === project.id && !f.projectId);
    const counts = new Map<string, number>();
    for (const s of projectSpecs)
      if (s.formaId) counts.set(s.formaId, (counts.get(s.formaId) || 0) + 1);
    const top =
      projectCopy?.id ||
      attached?.id ||
      [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ||
      [...allFormas]
        .filter((f: any) => !f.projectId && !isArchived(f))
        .sort((a: any, b: any) =>
          (a.docSettings?.standardName || 'Untitled').localeCompare(
            b.docSettings?.standardName || 'Untitled'
          )
        )[0]?.id ||
      '';
    setExportFormaId(top);
    setShowExportModal(true);
  }

  async function runExport() {
    const sortedSpecs = projectSpecs
      .slice()
      .sort((a, b) => (a.csiNumber || '').localeCompare(b.csiNumber || ''));
    const targetSpecs = sortedSpecs.filter((s) => exportSelIds.has(s.id));
    if (!targetSpecs.length) {
      ctx.toast('Select at least one spec to export', true);
      return;
    }
    if (!exportFormaId || !allFormas.some((f) => f.id === exportFormaId)) {
      ctx.toast('Create a forma first.', true);
      return;
    }

    // ── Export gate: refuse BEFORE asking for a folder ──
    const projectFormaCopyPre =
      allFormas.find((f: any) => f.projectId === project.id && !f.specId) || null;
    const dialogForma = exportFormaId
      ? allFormas.find((f) => f.id === exportFormaId) || null
      : null;
    const unboundAll = new Set<string>();
    let blockedCount = 0;
    for (const src of targetSpecs as any[]) {
      let bound: any = src.formaId ? allFormas.find((f) => f.id === src.formaId) : null;
      if (
        bound &&
        !bound.projectId &&
        projectFormaCopyPre &&
        projectFormaCopyPre.sourceFormaId === bound.id
      )
        bound = projectFormaCopyPre;
      const eff: any =
        allFormas.find((f: any) => f.specId === src.id) ||
        bound ||
        projectFormaCopyPre ||
        dialogForma;
      if (!eff) continue;
      const lastIss: any = realIssuances(src.issuances).slice(-1)[0] || null;
      const missing = unboundPlaceholders(eff, {
        project: {
          'Project.Name': project.name || '',
          'Project.ClientProjectName': (project as any).clientProjectName || project.name || '',
          'Project.Number': (project as any).projectNumber || '',
          'Project.Address': (project as any).address || '',
          'Client.Name': ctx.orgs.find((o) => o.id === (project as any).clientId)?.name || '',
          ...(exportDeliverable ? { Deliverable: exportDeliverable } : {}),
          'Issuance.Name': lastIss?.name || '',
        },
        issuances: (src.issuances || []) as any[],
        dateFormat: eff?.docSettings?.dateFormat,
      });
      if (missing.length) {
        blockedCount++;
        missing.forEach((id) => unboundAll.add(id));
      }
    }
    if (unboundAll.size) {
      const names = [...unboundAll].map((id) => (id === 'IssuanceDate' ? 'Issuance Date' : id));
      ctx.toast(
        `Set ${names.join(', ')} in the forma's Project Tokens before exporting — ` +
          `${blockedCount} spec${blockedCount > 1 ? 's have' : ' has'} a placeholder with no value.`,
        true
      );
      return;
    }

    let dir = exportDir;
    if (!dir && !isWeb) {
      dir = await (window as any).api?.selectFolderDialog?.();
      if (!dir) {
        ctx.toast('Pick an output folder first', true);
        return;
      }
      setExportDir(dir);
    }

    if (!isWeb && !(window as any).api?.exportProjectSpecs) {
      ctx.toast(
        'Export API not available — the desktop app needs to restart to pick up the new build.',
        true
      );
      return;
    }

    setExporting(true);
    try {
      const formaConfig = exportFormaId
        ? allFormas.find((f) => f.id === exportFormaId) || null
        : null;
      const clientForExport = (project as any).clientId
        ? ctx.orgs.find((o) => o.id === (project as any).clientId)
        : undefined;
      const specsPayload = await prepareSpecsForExport(
        targetSpecs,
        {
          project: {
            id: project.id || '',
            name: project.name,
            number: (project as any).projectNumber,
            address: (project as any).address,
            phase: (project as any).currentPhaseId,
            clientId: (project as any).clientId,
          },
          allProjectSpecs: projectSpecs,
          team: (ctx.members || []).map((tm: any) => ({
            id: tm.id,
            name: tm.name || tm.fullName || tm.displayName,
            email: tm.email,
            role: tm.role,
            title: tm.title,
          })),
          client: clientForExport,
        },
        { markRevisions: exportRevisionMarks }
      );
      const projectMeta: Record<string, string> = {
        'Project.Name': project.name || '',
        'Project.ClientProjectName': (project as any).clientProjectName || project.name || '',
        'Project.Number': (project as any).projectNumber || '',
        'Project.Address': (project as any).address || '',
        'Client.Name': clientForExport?.name || '',
      };
      if (exportDeliverable) {
        projectMeta['Deliverable'] = exportDeliverable;
        projectMeta['Deliverable.Name'] = exportDeliverable;
      }
      const liveProject: any = latestProjectRow(
        project as any,
        (await ProjectService.list().catch(() => [])) as any[]
      );
      for (const pt of (liveProject.formaTokens || []) as any[]) {
        projectMeta['ProjectToken:' + pt.id] = resolveProjectTokenContent(pt.content, projectMeta);
      }
      const projectFormaCopy =
        allFormas.find((f: any) => f.projectId === project.id && !f.specId) || null;
      const specsWithFormas = specsPayload.map((p, i) => {
        const src: any = targetSpecs[i];
        let bound: any = src.formaId ? allFormas.find((f) => f.id === src.formaId) : null;
        if (
          bound &&
          !bound.projectId &&
          projectFormaCopy &&
          projectFormaCopy.sourceFormaId === bound.id
        ) {
          bound = projectFormaCopy;
        }
        const eff: any =
          allFormas.find((f: any) => f.specId === src.id) || bound || projectFormaCopy;
        const lastIss: any = realIssuances(src.issuances).slice(-1)[0] || null;
        const issMeta: Record<string, string> = {
          'Issuance.Name': lastIss?.name || '',
          'Issuance.Date': lastIss?.date || lastIss?.issuedDate || '',
        };
        const perSpecMeta: Record<string, string> = { ...issMeta };
        for (const pt of (liveProject.formaTokens || []) as any[]) {
          perSpecMeta['ProjectToken:' + pt.id] = resolveProjectTokenContent(pt.content, {
            ...projectMeta,
            ...issMeta,
          });
        }
        const phCtx = {
          project: { ...projectMeta, ...issMeta },
          issuances: (src.issuances || []) as any[],
          dateFormat: (eff as any)?.docSettings?.dateFormat,
        };
        const phValues = eff ? resolvePlaceholders(eff as any, phCtx) : {};
        const missing = eff ? unboundPlaceholders(eff as any, phCtx) : [];
        const overrides = {
          ...perSpecMeta,
          ...phValues,
          ...(src.tokenOverrides && Object.keys(src.tokenOverrides).length
            ? src.tokenOverrides
            : {}),
        };
        return {
          ...p,
          formaConfig: eff ? JSON.parse(JSON.stringify(eff)) : undefined,
          metaOverrides: overrides,
          unboundPlaceholders: missing,
          specLabel: src.csiNumber || src.title || src.id,
        };
      });

      const blocked = specsWithFormas.filter((s: any) => s.unboundPlaceholders?.length);
      if (blocked.length) {
        const names = [...new Set(blocked.flatMap((s: any) => s.unboundPlaceholders))].map((id) =>
          id === 'IssuanceDate' ? 'Issuance Date' : id
        );
        ctx.toast(
          `Set ${names.join(', ')} in the forma's Project Tokens before exporting — ` +
            `${blocked.length} spec${blocked.length > 1 ? 's have' : ' has'} a placeholder with no value.`,
          true
        );
        setExporting(false);
        return;
      }
      specsWithFormas.forEach((s: any) => {
        delete s.unboundPlaceholders;
        delete s.specLabel;
      });
      const res = isWeb
        ? await exportSpecsViaServer({
            specs: specsWithFormas,
            formaConfig: formaConfig ? JSON.parse(JSON.stringify(formaConfig)) : null,
            projectMeta,
          })
        : await (window as any).api.exportProjectSpecs({
            specs: specsWithFormas,
            formaConfig: formaConfig ? JSON.parse(JSON.stringify(formaConfig)) : null,
            outputDir: dir,
            format: exportFormat,
            projectMeta,
          });
      const docxOk = (res?.docx || []).filter((r: any) => r.success).length;
      const pdfOk = (res?.pdf || []).filter((r: any) => r.success).length;
      const fails = [
        ...(res?.docx || []).filter((r: any) => !r.success),
        ...(res?.pdf || []).filter((r: any) => !r.success),
      ];
      if (fails.length) {
        // Failures get a results dialog (which file, why) instead of a console note.
        console.warn('[export] failures:', fails);
        setExportReport({
          okCount: docxOk + pdfOk,
          failures: fails.map((f) => ({
            file: String(('file' in f ? f.file : f.inputFile) || 'Unknown file'),
            reason: String(f.error || '').trim() || 'Unknown error',
          })),
          warnings: Array.isArray(res?.warnings) ? res.warnings.map(String) : [],
          folder: isWeb ? '' : exportDir,
        });
        setShowExportModal(false);
        return;
      } else {
        const parts: string[] = [];
        if (exportFormat !== 'pdf') parts.push(`${docxOk} docx`);
        if (exportFormat !== 'docx') parts.push(`${pdfOk} pdf`);
        ctx.toast(
          isWeb
            ? `Downloaded ${parts.join(' + ')}`
            : `Exported ${parts.join(' + ')} to ${exportDir}`
        );
      }
      if (!isWeb) (window as any).api?.openFolder?.(exportDir);
      setShowExportModal(false);
    } catch (err: any) {
      ctx.toast(`Export failed: ${err.message || err}`, true);
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-shrink-0 flex-wrap items-center gap-2.5 border-b border-border bg-surface px-5 py-2.5">
        {ctx.can.viewCompile && !ctx.locked && (
          <Button
            onClick={() => ctx.navigate?.({ view: 'compile', projectId: project.id })}
            title="Add master sections to this project"
          >
            <Icon icon={Plus} em={0.875} aria-hidden="true" />
            Add Specs
          </Button>
        )}
        {hasPermission(ctx.userPermissions, 'project_specs.compile') && !ctx.locked && (
          <Button
            variant="outline"
            onClick={() => void handleImportDocxToProject()}
            disabled={importingDocx}
            title="Import existing .docx specs straight into this project (bypasses the firm masters)"
          >
            <Icon icon={Upload} em={0.875} aria-hidden="true" />
            {importingDocx ? 'Importing…' : 'Import .docx'}
          </Button>
        )}
        {projectSpecs.length > 0 && ctx.can.exportProjectSpecs && (
          <Button variant="outline" onClick={openExportModal}>
            <Icon icon={Download} em={0.875} aria-hidden="true" />
            Export
          </Button>
        )}
        <span className="text-sm text-subtle">
          {projectSpecs.length} spec{projectSpecs.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-5">
        {projectSpecs.length === 0 ? (
          <EmptyState
            title="No specs in this project yet."
            subtitle="Pull master sections in to start building the project manual."
            action={
              ctx.can.viewCompile && !ctx.locked
                ? {
                    label: 'Add Specs',
                    onClick: () => ctx.navigate?.({ view: 'compile', projectId: project.id }),
                  }
                : undefined
            }
          />
        ) : (
          <DataTable<ProjectSpec>
            columns={specColumns}
            data={activeOnly(projectSpecs, showArchivedSpecs)}
            keyField="id"
            onRowClick={(s) => ctx.onOpenProjectSpec?.(s.id, project.id)}
            rowHref={
              ctx.onOpenProjectSpec
                ? (s) => toAddress({ kind: 'project-spec', projectId: project.id, specId: s.id })
                : undefined
            }
            groupOptions={specGroupOptions}
            searchPlaceholder="Search project specs..."
            initialSort={{ key: 'csiNumber', direction: 'asc' }}
            storageKey={`project-specs-${project.id}`}
            permissions={ctx.userPermissions}
            bulkActions={{
              // Server enforces `project_specs.delete` for removing specs from a
              // project (not the master-spec `specs.delete` key) — gate on the
              // real key directly rather than `ctx.can.deleteSpecs`. Both bulk
              // actions are also absent on a closed project (`!ctx.locked`) —
              // deleting/archiving specs is a write, this DataTable checks
              // `permission` against the raw permission list only, not lock state.
              delete:
                hasPermission(ctx.userPermissions, 'project_specs.delete') && !ctx.locked
                  ? {
                      permission: 'project_specs.delete',
                      onDelete: bulkDeleteProjectSpecs,
                      confirmTitle: 'Remove specs from this project?',
                    }
                  : undefined,
              archive: ctx.can.archiveSpecs
                ? {
                    permission: 'project_specs.archive',
                    onArchive: (items) => doArchiveProjectSpecs(items, true),
                    onUndo: (items) => doArchiveProjectSpecs(items, false),
                    label: 'Archive',
                  }
                : undefined,
              exportCsv: { permission: 'project_specs.export' },
              extra: (items, meta) => {
                const archivedOnes = items.filter((s) => isArchived(s));
                if (!archivedOnes.length || !ctx.can.archiveSpecs) return null;
                return (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      void doArchiveProjectSpecs(archivedOnes, false);
                      meta?.clear();
                    }}
                  >
                    Restore {archivedOnes.length}
                  </Button>
                );
              },
            }}
            toolbarExtra={
              <Button
                variant={showArchivedSpecs ? 'secondary' : 'outline'}
                size="sm"
                aria-pressed={showArchivedSpecs}
                onClick={() => setShowArchivedSpecs((v) => !v)}
              >
                {showArchivedSpecs ? 'Hide archived' : 'Show archived'}
              </Button>
            }
          />
        )}
      </div>

      <Dialog open={showExportModal} onOpenChange={(o) => !exporting && setShowExportModal(o)}>
        <DialogContent className="max-h-[85vh] max-w-xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Export Project Specs</DialogTitle>
            <DialogDescription>
              {exportSelIds.size} of {projectSpecs.length} spec
              {projectSpecs.length === 1 ? '' : 's'} selected · {project.name}
            </DialogDescription>
            {unpublishedSelected > 0 && (
              <span className="mt-1 block text-warning">
                {unpublishedSelected} spec{unpublishedSelected === 1 ? ' has' : 's have'}{' '}
                unpublished changes — {unpublishedSelected === 1 ? 'it exports' : 'they export'} as
                last published.
              </span>
            )}
          </DialogHeader>

          <div className="rounded-lg border border-border">
            <div className="flex items-center justify-between border-b border-border px-3 py-2">
              <span className="text-sm font-bold uppercase tracking-wide text-subtle">
                Specs to export
              </span>
              <Button
                variant="link"
                size="sm"
                onClick={() => {
                  const all = projectSpecs.map((s) => s.id);
                  setExportSelIds(exportSelIds.size === all.length ? new Set() : new Set(all));
                }}
              >
                {exportSelIds.size === projectSpecs.length ? 'Deselect all' : 'Select all'}
              </Button>
            </div>
            <div className="max-h-52 overflow-auto">
              {projectSpecs
                .slice()
                .sort((a, b) => (a.csiNumber || '').localeCompare(b.csiNumber || ''))
                .map((s) => {
                  const on = exportSelIds.has(s.id);
                  return (
                    <div
                      key={s.id}
                      onClick={() =>
                        setExportSelIds((prev) => {
                          const n = new Set(prev);
                          if (n.has(s.id)) n.delete(s.id);
                          else n.add(s.id);
                          return n;
                        })
                      }
                      className="flex min-h-8 cursor-pointer items-center gap-2.5 px-3 py-1.5 hover:bg-hover"
                    >
                      <Checkbox
                        checked={on}
                        onCheckedChange={(v) =>
                          setExportSelIds((prev) => {
                            const n = new Set(prev);
                            if (v) n.add(s.id);
                            else n.delete(s.id);
                            return n;
                          })
                        }
                        aria-label={`Include ${s.title || s.csiNumber || 'spec'} in export`}
                      />
                      <span className="min-w-[3rem] font-mono text-sm font-bold text-primary">
                        {s.csiNumber || '—'}
                      </span>
                      <span className="flex-1 truncate text-sm text-text">
                        {s.title || 'Untitled'}
                      </span>
                    </div>
                  );
                })}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold uppercase tracking-wide text-subtle">Forma</span>
            <SearchableSelect
              value={exportFormaId}
              onChange={(v) => v && setExportFormaId(v)}
              options={exportFormaOptions}
              placeholder="Choose a forma"
            />
            <span className="text-xs text-subtle">
              {exportFormaOptions.length === 0
                ? 'Create a forma first.'
                : 'Formatting template applied to every exported spec.'}
            </span>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold uppercase tracking-wide text-subtle">
              Deliverable
            </span>
            <SearchableSelect
              value={exportDeliverable}
              onChange={(v) => setExportDeliverable(v || '')}
              options={exportDeliverables.map((d) => ({ value: d.name, label: d.name }))}
              placeholder="No deliverable stamp"
              clearable
            />
            <span className="text-xs text-subtle">
              Fills the {'{Deliverable}'} token in the forma — e.g. headers, footers, cover. Leave
              blank if this export isn't tied to a deliverable.
            </span>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold uppercase tracking-wide text-subtle">
              Format
            </span>
            <div className="flex gap-1.5">
              {(['docx', 'pdf', 'both'] as const)
                .filter((f) => !isWeb || f === 'docx')
                .map((f) => (
                  <Button
                    key={f}
                    variant={exportFormat === f ? 'default' : 'outline'}
                    className="flex-1 uppercase"
                    onClick={() => setExportFormat(f)}
                  >
                    {f === 'both' ? 'Both' : f}
                  </Button>
                ))}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-semibold uppercase tracking-wide text-subtle">
              Revision Marks
            </span>
            <div className="flex gap-1.5">
              {(
                [
                  { v: false, label: 'Clean' },
                  { v: true, label: 'Mark changes' },
                ] as const
              ).map((opt) => (
                <Button
                  key={String(opt.v)}
                  variant={exportRevisionMarks === opt.v ? 'default' : 'outline'}
                  className="flex-1"
                  onClick={() => setExportRevisionMarks(opt.v)}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
            <span className="text-xs text-subtle">
              Mark changes = strike/underline/highlight every edit since each spec&rsquo;s last
              issuance, in that spec&rsquo;s revision style. Specs never issued export clean.
            </span>
          </div>

          {isWeb ? (
            <div className="text-sm text-subtle">
              Files download to your browser&rsquo;s download folder.
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold uppercase tracking-wide text-subtle">
                Output Folder
              </span>
              <div className="flex gap-1.5">
                <Input
                  value={exportDir}
                  readOnly
                  placeholder="Choose a folder…"
                  className="flex-1"
                />
                <Button
                  variant="outline"
                  onClick={async () => {
                    const dir = await (window as any).api?.selectFolderDialog?.();
                    if (dir) setExportDir(dir);
                  }}
                >
                  Browse
                </Button>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              disabled={exporting}
              onClick={() => setShowExportModal(false)}
            >
              Cancel
            </Button>
            <Button disabled={exporting || !exportFormaId} onClick={() => void runExport()}>
              {exporting ? 'Exporting…' : 'Export'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export finished — only opens when at least one file failed */}
      <Dialog open={!!exportReport} onOpenChange={(o) => !o && setExportReport(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Export finished</DialogTitle>
            <DialogDescription>
              {exportReport?.okCount ?? 0} file{exportReport?.okCount === 1 ? '' : 's'} exported.{' '}
              {exportReport?.failures.length ?? 0} didn&rsquo;t.
            </DialogDescription>
          </DialogHeader>
          <ul className="flex max-h-64 flex-col gap-1.5 overflow-auto text-sm">
            {(exportReport?.failures ?? []).map((f, i) => (
              <li key={i} className="flex flex-col rounded-md bg-surface-alt px-3 py-2">
                <span className="font-semibold text-text">{f.file}</span>
                <span className="text-danger">{f.reason}</span>
              </li>
            ))}
            {(exportReport?.warnings ?? []).map((w, i) => (
              <li key={'w' + i} className="flex flex-col rounded-md bg-surface-alt px-3 py-2">
                <span className="font-semibold text-text">Note</span>
                <span className="text-subtle">{w}</span>
              </li>
            ))}
          </ul>
          <DialogFooter>
            {exportReport?.folder ? (
              <Button
                variant="outline"
                onClick={() => window.api?.openFolder?.(exportReport.folder)}
              >
                Open folder
              </Button>
            ) : null}
            <Button onClick={() => setExportReport(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import: files that could not be brought in */}
      <Dialog open={!!importFailures} onOpenChange={(o) => !o && setImportFailures(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Some files weren&rsquo;t imported</DialogTitle>
            <DialogDescription>
              {importFailures?.length ?? 0} file{importFailures?.length === 1 ? '' : 's'}{' '}
              couldn&rsquo;t be imported.
            </DialogDescription>
          </DialogHeader>
          <ul className="flex max-h-64 flex-col gap-1.5 overflow-auto text-sm">
            {(importFailures ?? []).map((f, i) => (
              <li key={i} className="flex flex-col rounded-md bg-surface-alt px-3 py-2">
                <span className="font-semibold text-text">{f.file}</span>
                <span className="text-danger">{f.reason}</span>
              </li>
            ))}
          </ul>
          <DialogFooter>
            <Button onClick={() => setImportFailures(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
